import { CommonModule, DOCUMENT } from '@angular/common';
import type { OnInit } from '@angular/core';
import { Component, inject, Input, Renderer2 } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import type { PageDataResponse } from '@core-mkt/resolvers/page-data/page-data-response';
import { BrandConfigurationService } from '@core-mkt/services/configuration/brand-configuration.service';
import { DateService } from '@core-mkt/services/date/date.service';
import { EnvService } from '@core-mkt/services/env/env.service';
import { ImgixService } from '@core-mkt/services/imgix/imgix.service';
import { getParsedLinkString, getQueryParamString } from '@core-mkt/services/query-param/query-helpers';
import { QueryParamDirective } from '@core-mkt/services/query-param/query-param.directive';
import { QueryParamService } from '@core-mkt/services/query-param/query-param.service';
import { canonicalIdBase } from '@core-mkt/services/schema/schema-id.util';
import { getBrandSiteUrl } from '@core-mkt/services/schema/organization.schema';
import { SchemaService } from '@core-mkt/services/schema/schema.service';
import type { ComponentTheme, ThemeInterface } from '@core-mkt/services/theme-parser/theme-parser.service';
import { ThemeParserService } from '@core-mkt/services/theme-parser/theme-parser.service';
import { WysiwygRedactorService } from '@core-mkt/services/wysiwg-redactor/wysiwyg-redactor.service';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faArrowLeft, faArrowRight, faEllipsisH } from '@fortawesome/free-solid-svg-icons';
import { cloneDeep } from 'lodash-es';
import { EntryListingArticleCardComponent } from '../entry-listing-article-card/entry-listing-article-card.component';
import type { BlogEntry, EntryListingArticleCard } from './entry-listings';
import { EntryListings } from './entry-listings';
import { getEntryListingsSchema } from './entry-listings.schema';

@Component({
  selector: 'entry-listings',
  templateUrl: './entry-listings.component.html',
  styleUrls: ['./entry-listings.component.scss'],
  standalone: true,
  imports: [CommonModule, FormsModule, FontAwesomeModule, EntryListingArticleCardComponent, QueryParamDirective],
})
export class EntryListingsComponent implements OnInit {
  @Input() data: EntryListings;
  activeIndex = 0;
  selectedStateFilter = '';
  selectedCategoryId = '';
  categoryList: Array<{ id: string; title: string; slug: string }> = [];
  stateList: Array<{ id: string; title: string; slug: string }> = [];
  pages = [];
  pageWindowIndex = 0;
  theme: ThemeInterface;
  entryListingContent: string | undefined = undefined;
  faArrowRight = faArrowRight;
  faArrowLeft = faArrowLeft;
  faEllipsisH = faEllipsisH;
  originalEntryListingData: EntryListingArticleCard[];
  paginationBaseUrl: string;
  hasOtherParams: boolean;
  resolverData: PageDataResponse;
  blogEntriesData: BlogEntry[] | null = null;
  totalBlogPages = 0;
  blogPath: string;
  private rawListingsTitle: string | undefined;
  private queryParamService = inject(QueryParamService);

  private document = inject<Document>(DOCUMENT);
  private themeParser = inject(ThemeParserService);
  private redactor = inject(WysiwygRedactorService);
  private imgixService = inject(ImgixService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private renderer = inject(Renderer2);
  private dateService = inject(DateService);
  private brandConfig = inject(BrandConfigurationService);
  private envService = inject(EnvService);
  private schemaService = inject(SchemaService);

  constructor() {
    // Get blog path from environment blogPrefix (e.g., 'blog--' becomes '/blog')
    const blogPrefix = this.envService.get.blogPrefix || 'blog--';
    this.blogPath = `/${this.slugToPath(blogPrefix).replace(/\/$/, '')}`;
    if (this.route.snapshot) {
      this.resolverData = cloneDeep(this.route.snapshot.data.pageData) as PageDataResponse;
    }
    // Check if blog entries were already loaded from the resolver
    if (this.resolverData?.craftData?.pageBlogEntries?.length > 0) {
      this.blogEntriesData = this.resolverData.craftData.pageBlogEntries;

      // Also get the total count from resolver
      if (this.resolverData.craftData.pageBlogEntriesCount) {
        const limit = 10;
        this.totalBlogPages = Math.ceil(this.resolverData.craftData.pageBlogEntriesCount / limit);
      }

      // Get categories from resolver
      const blogCategories = this.resolverData.craftData.blogCategories || [];
      this.categoryList = blogCategories;

      // Get state categories from resolver
      const blogStateCategories = this.resolverData.craftData.blogStateCategories || [];
      this.stateList = blogStateCategories;
    }
  }

  private parseReasourceContent(summary: string, title: string): string {
    return `<p class="large"><strong>${title}</strong></p><p>${summary}</p>`;
  }

  /**
   * Processes blog entries and maps them to entry listing article cards.
   */
  private processBlogEntries(): void {
    if (!this.blogEntriesData || this.blogEntriesData.length === 0) {
      return;
    }
    const pagesBlogEntries: EntryListingArticleCard[] = this.blogEntriesData
      .filter((p) => p.blogSummary)
      .map((p) => ({
        image: p.featuredEntryImage,
        relatedResourceContent: this.parseReasourceContent(p.blogSummary, p.title),
        relatedResourceLink: `/${this.slugToPath(p.slug)}`,
        relatedResourceCtaLabel: 'Read More',
        relatedResourcesCategory: p.blogCategories,
        entryListingArticleCards: [],
        state: p.pageState,
        dateCreated: this.dateService.getDateFormatted(p.postDate),
      }));

    this.data.entryListingArticleCards = pagesBlogEntries;
  }

  get entryListingSideContent(): string | undefined {
    return this.entryListingContent;
  }

  get entryListingsTitle(): string | undefined {
    return this.data.entryListingsTitle;
  }

  get entryListingArticleCards(): EntryListingArticleCard[] | undefined {
    return this.data.entryListingArticleCards;
  }

  get filterStateTitle(): string {
    return this.data.stateFilterTitle ? this.data.stateFilterTitle : 'Filter by State';
  }

  get filterStateDefaultLabel(): string {
    return this.data.stateFilterDefaultLabel ? this.data.stateFilterDefaultLabel : 'All States';
  }

  get filterCategoryTitle(): string {
    return this.data.categoryFilterTitle ? this.data.categoryFilterTitle : 'Categories';
  }

  ngOnInit(): void {
    // Capture raw title before bypassSanitizer wraps it as a SafeValue —
    // the schema function needs a plain string for ItemList.name.
    const rawListingsTitle =
      typeof this.data.entryListingsTitle === 'string' ? this.data.entryListingsTitle : undefined;
    this.rawListingsTitle = rawListingsTitle;
    this.data.entryListingsTitle = this.redactor.bypassSanitizer(this.data.entryListingsTitle);
    if (this.data?.entryListingSideContent) {
      this.entryListingContent = this.redactor.bypassSanitizer(this.data.entryListingSideContent);
    }

    // Initialize entryListingArticleCards if undefined
    if (!this.data.entryListingArticleCards) {
      this.data.entryListingArticleCards = [];
    }

    // Restore selected category from query params
    const catParam = this.route.snapshot.queryParamMap.get('cat');
    if (catParam) {
      this.selectedCategoryId = catParam;
    }

    // Restore selected state from query params
    const stateParam = this.route.snapshot.queryParamMap.get('state');
    if (stateParam) {
      this.selectedStateFilter = stateParam;
    }

    // Process blog entries if they were loaded from the resolver
    // The resolver loads the data, so it's available in both SSR and non-SSR modes
    if (this.blogEntriesData && this.blogEntriesData.length > 0) {
      this.processBlogEntries();
    }

    const themeInput: ComponentTheme = {
      styles: this.data.styles,
      backgroundColor: this.data.backgroundColor,
      textColor: this.data.textColor,
      columnCount: this.data.entryListingArticleCards.length,
      columnDistribution: undefined,
      backgroundImage: undefined,
    };
    this.theme = this.themeParser.getThemeClasses(themeInput);
    this.originalEntryListingData = [...this.data.entryListingArticleCards];

    // For server-side pagination, create pages based on totalBlogPages
    if (this.totalBlogPages > 0) {
      this.pages = Array.from({ length: this.totalBlogPages }, () => []);
      const pageParam = this.route.snapshot.queryParamMap.get('page');
      const currentPage = pageParam ? parseInt(pageParam, 10) : 1;
      this.activeIndex = currentPage - 1;
      // Put the current page's entries into the pages array
      if (this.data.entryListingArticleCards && Array.isArray(this.data.entryListingArticleCards)) {
        this.pages[this.activeIndex] = this.data.entryListingArticleCards;
      }
      // Initialize sliding window indices
      this.updateSlidingWindowIndices();
    } else {
      // Fallback to client-side pagination for non-blog entries
      if (this.data.entryListingArticleCards && Array.isArray(this.data.entryListingArticleCards)) {
        this.getNumberOfPages([...this.data.entryListingArticleCards] as EntryListingArticleCard[]);
        const pageParam = this.route.snapshot.queryParamMap.get('page');
        if (pageParam) {
          const pageNumber = parseInt(pageParam, 10);
          if (pageNumber > 0 && pageNumber <= this.pages.length) {
            this.changePage(pageNumber - 1);
          }
        } else {
          // Initialize sliding window indices for default page
          this.updateSlidingWindowIndices();
        }
      }
    }

    this.setPaginationBaseUrl();
    this.updateCanonicalUrl();
    this.registerSchema();
    this.updatePaginationLinkTags();
  }

  /**
   * Registers BlogPosting + ItemList schema for the blog entries on this page.
   * Captures the listing title before bypassSanitizer wraps it as SafeValue.
   */
  private registerSchema(): void {
    if (!this.blogEntriesData || this.blogEntriesData.length === 0) return;
    const organizationName = this.envService.get?.brandConfig?.name ?? '';
    const brandUrl = this.envService.get?.brandConfig?.url;
    // Skip when brand config can't yield a valid Organization — avoids an invalid
    // siteUrl and broken #organization cross-references.
    if (!organizationName || !brandUrl) return;
    // Canonical (origin + pathname, no query/hash) so ItemList/BlogPosting @ids stay
    // stable across ?page=/UTM/coupon variants of the same listing page.
    const pageUrl = canonicalIdBase(this.document.URL);
    // Canonical brand site root for the org cross-ref and article URLs — never the
    // runtime document.URL, which would fragment the org @id per environment. EPS-3951.
    const siteUrl = getBrandSiteUrl(brandUrl);

    this.schemaService.register(
      getEntryListingsSchema({
        pageUrl,
        siteUrl,
        organizationName,
        listTitle: this.rawListingsTitle,
        blogEntries: this.blogEntriesData,
      }),
    );
  }

  private slugToPath(slug: string): string {
    return slug.replace(/--/g, '/');
  }

  getCategoryFilterTargetUrl(slug: string, id: string): string {
    const baseUrl = slug && id ? `${this.blogPath}/${slug}/` : this.blogPath;
    return this.buildFilterTargetUrl(baseUrl, { cat: slug && id ? id : undefined });
  }

  getStateFilterTargetUrl(slug: string, id: string): string {
    return this.buildFilterTargetUrl(`${this.blogPath}/`, { state: slug && id ? slug : undefined });
  }

  onCategoryFilterChange(slug: string, id: string): void {
    window.location.href = this.getCategoryFilterTargetUrl(slug, id);
  }

  onStateFilterChange(slug: string, id: string): void {
    window.location.href = this.getStateFilterTargetUrl(slug, id);
  }

  private static readonly BLOG_FILTER_KEYS = ['cat', 'state'];

  private buildFilterTargetUrl(baseUrl: string, overrides: Record<string, string | undefined>): string {
    // Filter overrides (cat/state) lead the query string; non-overridden blog filter
    // params (cat/state) are preserved next so picking a category keeps an active state
    // filter and vice-versa; inherited tracking params (utm/gclid/etc.) trail. The
    // shared helpers would otherwise strip cat/state on slug-based destinations because
    // they're blog-only params, so we bake them into the destination URL before merging.
    const inherited = { ...this.queryParamService.getQueryParams() };
    const leadingParams = new URLSearchParams();

    Object.entries(overrides).forEach(([key, value]) => {
      if (value !== undefined && value !== '') {
        leadingParams.set(key, value);
      }
      delete inherited[key];
    });

    EntryListingsComponent.BLOG_FILTER_KEYS.forEach((key) => {
      if (inherited[key]) {
        leadingParams.set(key, inherited[key]);
        delete inherited[key];
      }
    });

    const leadingQuery = leadingParams.toString();
    const overrideUrl = leadingQuery ? `${baseUrl}?${leadingQuery}` : baseUrl;

    const blogPrefix = this.envService.get.blogPrefix;
    const inheritedQueryString = getQueryParamString(inherited, baseUrl, blogPrefix);
    return getParsedLinkString(
      overrideUrl,
      inheritedQueryString,
      this.brandConfig.hostName,
      this.brandConfig.siblingSites,
      blogPrefix,
    );
  }

  getNumberOfPages(entryListingArticleCards: EntryListingArticleCard[]): void {
    this.goToFirstPage();
    this.pages = [];
    const articleCards = entryListingArticleCards;
    while (articleCards.length > 0) {
      this.pages.push(articleCards.splice(0, 10));
    }
  }

  private updateSlidingWindowIndices(): void {
    const index = this.activeIndex;

    // Calculate sliding window (show 4 pages at a time on all devices)
    // Keep active page in the middle when possible
    if (index <= 1) {
      this.pageWindowIndex = 0;
    } else if (index >= this.pages.length - 2) {
      this.pageWindowIndex = Math.max(0, this.pages.length - 4);
    } else {
      this.pageWindowIndex = index - 1;
    }
  }

  changePage(index: number): void {
    this.activeIndex = index;
    this.updateSlidingWindowIndices();
    this.updateUrlWithPageParam(index + 1);
    this.updateCanonicalUrl();
    this.updatePaginationLinkTags();
  }

  goToNextPage(): void {
    if (this.activeIndex < this.pages.length) {
      this.changePage(this.activeIndex + 1);
    }
  }

  goToFirstPage(): void {
    this.changePage(0);
  }

  goToPreviousPage(): void {
    if (this.activeIndex > 0) {
      this.changePage(this.activeIndex - 1);
    }
  }

  skip(direction: string): void {
    let skipIdx = this.activeIndex + 2;
    if (direction === 'back') {
      skipIdx = this.activeIndex - 2;
    }
    this.changePage(skipIdx);
  }

  scroll(el: HTMLElement): void {
    el.scrollIntoView({
      behavior: 'auto',
      block: 'start',
      inline: 'start',
    });
  }

  private updateUrlWithPageParam(pageNumber: number): void {
    const queryParams = { ...this.route.snapshot.queryParams };

    if (pageNumber > 1) {
      queryParams['page'] = pageNumber.toString();
    } else {
      delete queryParams['page'];
    }

    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: queryParams,
      replaceUrl: true,
    });
  }

  private updateCanonicalUrl(): void {
    const pageNumber = this.activeIndex + 1;

    // Get the existing canonical URL (set by SEO service)
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
    const canonicalLink = this.document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (canonicalLink?.href) {
      const url = new URL(canonicalLink.href);

      // Remove existing page parameter if it exists
      url.searchParams.delete('page');

      // Add page parameter if not on first page
      if (pageNumber > 1) {
        url.searchParams.set('page', pageNumber.toString());
      }

      this.renderer.setAttribute(canonicalLink, 'href', url.toString());
    }
  }

  /**
   * Emits rel=prev / rel=next link tags in the <head> so crawlers can discover the
   * sequential order of paginated listing pages (EPS-4098). Mirrors updateCanonicalUrl():
   * the absolute root URL is derived from the canonical link (set by the SEO service).
   *
   * - rel=prev: present when currentPage > 1; page 2 points to the root URL (no ?page=1)
   * - rel=next: present when currentPage < total page count
   * Non-paginated listings (a single page) emit neither tag.
   */
  private updatePaginationLinkTags(): void {
    // Clear any tags from a previous render (client-side page navigation)
    this.removePaginationLinkTags();

    // Prefer the authoritative server-side total: this.pages can grow sparsely when an
    // out-of-range ?page sets this.pages[activeIndex], which would inflate this.pages.length
    // past the real total and let the bounds guard below emit tags for a non-existent page.
    const pageCount = this.totalBlogPages > 0 ? this.totalBlogPages : this.pages.length;
    const currentPage = this.activeIndex + 1;
    // Only emit on multi-page listings, and only when the active page is in range.
    // (Guards against a stale/out-of-bounds activeIndex emitting tags for a missing page.)
    if (pageCount <= 1 || currentPage < 1 || currentPage > pageCount) {
      return;
    }

    // Read the resolved (absolute) href so new URL() can't throw on a relative value.
    const canonicalHref = this.document.querySelector<HTMLLinkElement>('link[rel="canonical"]')?.href;
    if (!canonicalHref) {
      return;
    }

    // Derive the clean root URL regardless of the page param currently on the canonical
    // (any non-page params the canonical carries are preserved).
    const baseUrl = new URL(canonicalHref);
    baseUrl.searchParams.delete('page');

    if (currentPage > 1) {
      const prevUrl = new URL(baseUrl.toString());
      // Page 2 -> root URL (no ?page=1); earlier pages keep ?page=N-1
      if (currentPage - 1 > 1) {
        prevUrl.searchParams.set('page', (currentPage - 1).toString());
      }
      this.appendPaginationLinkTag('prev', prevUrl.toString());
    }

    if (currentPage < pageCount) {
      const nextUrl = new URL(baseUrl.toString());
      nextUrl.searchParams.set('page', (currentPage + 1).toString());
      this.appendPaginationLinkTag('next', nextUrl.toString());
    }
  }

  private appendPaginationLinkTag(rel: 'prev' | 'next', href: string): void {
    const link: HTMLLinkElement = this.document.createElement('link');
    this.renderer.setAttribute(link, 'rel', rel);
    this.renderer.setAttribute(link, 'href', href);
    // Mark links this component owns so removal only ever targets ours, never unrelated rel=prev/next.
    this.renderer.setAttribute(link, 'data-pagination-rel', '');
    this.renderer.appendChild(this.document.head, link);
  }

  private removePaginationLinkTags(): void {
    const existingLinks = this.document.head.querySelectorAll('link[data-pagination-rel]');
    existingLinks.forEach((link) => this.renderer.removeChild(this.document.head, link));
  }

  private setPaginationBaseUrl(): void {
    const queryParams = { ...this.route.snapshot.queryParams };
    delete queryParams['page']; // Remove page param to get clean base

    this.hasOtherParams = Object.keys(queryParams).length > 0;

    if (this.hasOtherParams) {
      const queryString = Object.keys(queryParams)
        .map((key) => `${key}=${queryParams[key] as string}`)
        .join('&');
      this.paginationBaseUrl = this.router.url.split('?')[0] + '?' + queryString;
    } else {
      this.paginationBaseUrl = this.router.url.split('?')[0];
    }
  }

  getPageUrl(pageIndex: number): string {
    // Map page 1 (index 0) and any non-positive index to the clean root URL. The
    // "previous" arrow calls getPageUrl(activeIndex - 1), so on page 1 (activeIndex 0)
    // this would otherwise emit ?page=0 into the SSR HTML — a non-canonical URL crawlers
    // follow before JS hydration hides the link (EPS-4099).
    if (pageIndex <= 0) {
      return this.paginationBaseUrl; // Page 1, no page parameter needed
    }

    const separator = this.hasOtherParams ? '&' : '?';
    return `${this.paginationBaseUrl}${separator}page=${pageIndex + 1}`;
  }
}
